import { useEffect, useRef } from 'react';
import { useActiveTrackingScripts, TrackingScript } from '@/hooks/useTrackingScripts';

/**
 * Validates that a script_id only contains safe characters (alphanumeric, dashes, underscores, dots)
 */
function isValidScriptId(id: string | null): boolean {
  if (!id) return false;
  return /^[a-zA-Z0-9\-_.]+$/.test(id);
}

/**
 * Gera o código do script baseado no tipo
 */
function generateScriptCode(script: TrackingScript): { code: string; async?: boolean; src?: string } | null {
  // For known types, validate script_id format to prevent injection
  if (script.type !== 'custom' && !isValidScriptId(script.script_id)) {
    console.warn(`[TrackingScripts] Invalid script_id blocked for type=${script.type}`);
    return null;
  }
  switch (script.type) {
    case 'gtm':
      return {
        code: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${script.script_id}');`,
      };

    case 'ga4':
      return {
        src: `https://www.googletagmanager.com/gtag/js?id=${script.script_id}`,
        async: true,
        code: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${script.script_id}');`,
      };

    case 'meta':
      return {
        code: `!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${script.script_id}');
fbq('track', 'PageView');`,
      };

    case 'tiktok':
      return {
        code: `!function (w, d, t) {
  w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
  ttq.load('${script.script_id}');
  ttq.page();
}(window, document, 'ttq');`,
      };

    case 'linkedin':
      return {
        code: `_linkedin_partner_id = "${script.script_id}";
window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
window._linkedin_data_partner_ids.push(_linkedin_partner_id);
(function(l) {
if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
window.lintrk.q=[]}
var s = document.getElementsByTagName("script")[0];
var b = document.createElement("script");
b.type = "text/javascript";b.async = true;
b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
s.parentNode.insertBefore(b, s);})(window.lintrk);`,
      };

    case 'custom': {
      const code = (script.custom_code || '').trim();
      // Block obvious dangerous patterns in custom scripts
      if (!code || code.length > 10000) {
        console.warn(`[TrackingScripts] Custom script blocked: empty or exceeds size limit`);
        return null;
      }
      return { code };
    }

    default:
      return { code: '' };
  }
}

/**
 * Creates a safe GTM noscript iframe element (avoids innerHTML)
 */
function createGtmNoscriptIframe(scriptId: string): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(scriptId)}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  return iframe;
}

export function TrackingScriptsLoader() {
  const { scripts, loading } = useActiveTrackingScripts();
  const injectedScriptsRef = useRef<HTMLElement[]>([]);
  const hasInjectedRef = useRef(false);

  useEffect(() => {
    if (loading || scripts.length === 0 || hasInjectedRef.current) return;

    // Defer tracking scripts to after page is interactive
    const injectScripts = () => {
      // Limpa scripts anteriores antes de injetar novos
      injectedScriptsRef.current.forEach((el) => {
        try {
          el.parentNode?.removeChild(el);
        } catch (e) {
          // Elemento pode já ter sido removido
        }
      });
      injectedScriptsRef.current = [];

    scripts.forEach((script) => {
      const generated = generateScriptCode(script);
      if (!generated) return; // Blocked by validation
      
      // Se tem src externo (como GA4), adiciona primeiro
      if (generated.src) {
        const externalScript = document.createElement('script');
        externalScript.src = generated.src;
        externalScript.async = generated.async ?? true;
        externalScript.setAttribute('data-tracking-id', script.id);
        
        if (script.position === 'head') {
          document.head.appendChild(externalScript);
        } else {
          document.body.appendChild(externalScript);
        }
        injectedScriptsRef.current.push(externalScript);
      }

      // Adiciona o código inline
      if (generated.code) {
        const inlineScript = document.createElement('script');
        inlineScript.textContent = generated.code;
        inlineScript.setAttribute('data-tracking-id', script.id);
        
        switch (script.position) {
          case 'head':
            document.head.appendChild(inlineScript);
            break;
          case 'body_start':
            document.body.insertBefore(inlineScript, document.body.firstChild);
            break;
          case 'body_end':
          default:
            document.body.appendChild(inlineScript);
            break;
        }
        injectedScriptsRef.current.push(inlineScript);
      }

      // Adiciona noscript para GTM (safe DOM creation, no innerHTML)
      if (script.type === 'gtm' && script.script_id) {
        const noscript = document.createElement('noscript');
        noscript.appendChild(createGtmNoscriptIframe(script.script_id));
        noscript.setAttribute('data-tracking-id', script.id);
        document.body.insertBefore(noscript, document.body.firstChild);
        injectedScriptsRef.current.push(noscript);
      }
    });
    
    hasInjectedRef.current = true;
    };

    // Delay injection until after initial render and user interaction to improve TTI/TBT
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(injectScripts, { timeout: 5000 });
    } else {
      setTimeout(injectScripts, 4000);
    }

    // Cleanup ao desmontar
    return () => {
      injectedScriptsRef.current.forEach((el) => {
        try {
          el.parentNode?.removeChild(el);
        } catch (e) {
          // Elemento pode já ter sido removido
        }
      });
      injectedScriptsRef.current = [];
      hasInjectedRef.current = false;
    };
  }, [scripts, loading]);

  // Este componente não renderiza nada visualmente
  return null;
}
